import React, { useEffect, useRef, useState } from 'react';
import Header from '../../layouts/Header';
import { Button, Col, Row, Form, Table, Spinner, Pagination, Dropdown } from 'react-bootstrap';
import { Modal } from 'react-bootstrap';
import makeAnimated from "react-select/animated";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import { useSelector } from 'react-redux';
import axiosInstance from '../../api/axios';

// ─── DatePicker input wrapper — make it inherit inputStyle width ──────────────
const datepickerWrapStyle = { width: '100%', display: 'block' };

// ─── Shared style tokens ───────────────────────────────────────────────────────
const T = {
    blue:        '#185FA5',
    blueDark:    '#0C447C',
    blueLight:   '#E6F1FB',
    green:       '#3B6D11',
    greenLight:  '#EAF3DE',
    red:         '#A32D2D',
    redLight:    '#FCEBEB',
    amber:       '#BA7517',
    purple:      '#534AB7',
    purpleLight: '#EEEDFE',
    border:      'rgba(0,0,0,0.10)',
    borderMid:   'rgba(0,0,0,0.16)',
    bg:          '#F7F7F8',
    surface:     '#FFFFFF',
    textPrimary: '#1A1A1A',
    textMuted:   '#6B6B6B',
    textFaint:   '#9E9E9E',
};

const inputStyle = {
    height: '34px',
    border: `0.5px solid ${T.borderMid}`,
    borderRadius: '4px',
    fontSize: '13px',
    padding: '0 10px',
    fontFamily: 'inherit',
    color: T.textPrimary,
    background: T.surface,
    outline: 'none',
    width: '100%',
};

const labelStyle = {
    fontSize: '11px',
    fontWeight: '500',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: T.textFaint,
    marginBottom: '4px',
    display: 'block',
};

const primaryBtn = {
    height: '34px',
    padding: '0 16px',
    borderRadius: '4px',
    border: `1px solid ${T.blue}`,
    background: T.blue,
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'inherit',
};

const ghostBtn = {
    height: '34px',
    padding: '0 16px',
    borderRadius: '4px',
    border: `0.5px solid ${T.borderMid}`,
    background: T.surface,
    color: T.textMuted,
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontFamily: 'inherit',
};

const dangerBtn = {
    ...primaryBtn,
    background: T.red,
    border: `1px solid ${T.red}`,
};

const selectStyles = {
    control: (p, s) => ({
        ...p,
        minHeight: '34px',
        height: 'auto',
        border: `0.5px solid ${s.isFocused ? T.blue : T.borderMid}`,
        borderRadius: '4px',
        boxShadow: s.isFocused ? `0 0 0 2px rgba(24,95,165,0.12)` : 'none',
        fontSize: '13px',
        '&:hover': { borderColor: T.borderMid },
    }),
    placeholder: (p) => ({ ...p, color: T.textFaint, fontSize: '13px' }),
    option: (p, s) => ({
        ...p,
        fontSize: '13px',
        background: s.isSelected ? T.blue : s.isFocused ? T.blueLight : '#fff',
        color: s.isSelected ? '#fff' : T.textPrimary,
    }),
};

const StatusDot = ({ status }) => {
    const active = status === 'ACTIVE';
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: '500', color: active ? T.green : T.red }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: active ? T.green : T.red, flexShrink: 0 }} />
            {active ? 'Active' : 'Inactive'}
        </span>
    );
};

const orderColor = (type) => {
    if (type === 'PICKUP')   return T.purple;
    if (type === 'DELIVERY') return T.green;
    return T.amber;
};

const modalHeaderStyle = {
    borderBottom: `0.5px solid ${T.border}`,
    padding: '14px 20px',
    background: T.surface,
};
const modalFooterStyle = {
    borderTop: `0.5px solid ${T.border}`,
    padding: '12px 20px',
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
};

// ─── Field wrapper — defined OUTSIDE Category so it never remounts on re-render ─
const Field = ({ label, required, children }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <label style={labelStyle}>
            {label}{required && <span style={{ color: '#A32D2D', marginLeft: 2 }}>*</span>}
        </label>
        {children}
    </div>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const Category = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const { access } = useSelector((state) => state.login);
    const { email } = useSelector((state) => state.login);
    const [menuAccess, setMenuAccess] = useState([]);

    useEffect(() => {
        console.log("All Access :", access);
        
        if (Array.isArray(access)) {
            const filteredAccess = access?.filter((perm) => perm?.startsWith("CategoryMgt:"));
            setMenuAccess(filteredAccess);
        }
    }, [access]);

    console.log("Category Access :", menuAccess);
    console.log("Email of the admin user :", email);

    const accessToken = localStorage.getItem("authToken");

    // 'normal' | 'featured'
    const [addCategoryType, setAddCategoryType] = useState('normal');

    const [inputData, setInputData] = useState({ sequenceNumber: "", name: "", description: "", isEventCategory: "", orderType: "ALL" });

    // Featured category state
    const [featuredData, setFeaturedData] = useState({ name: "", description: "", sequenceNumber: "", validityStartDate: "", validityEndDate: "" });
    const [featuredProducts, setFeaturedProducts] = useState([
        { product: "", sequenceNumber: 1, excludedOutlets: [], includedOutlets: [] }
    ]);
    const [isFeaturedLoading, setIsFeaturedLoading] = useState(false);
    const [filteredProductList, setFilteredProductList] = useState([]);
    const [activeTab, setActiveTab] = useState('normal');           // 'normal' | 'featured'
    const [featuredCategoryList, setFeaturedCategoryList] = useState([]);
    const [isFeaturedListLoading, setIsFeaturedListLoading] = useState(false);

    // Featured edit / delete / image states
    const [openFeaturedEditModal, setOpenFeaturedEditModal]             = useState(false);
    const [openFeaturedDeleteModal, setOpenFeaturedDeleteModal]         = useState(false);
    const [openFeaturedImgModal, setOpenFeaturedImgModal]               = useState(false);
    const [openFeaturedBannerModal, setOpenFeaturedBannerModal]         = useState(false);
    const [selectedFeatured, setSelectedFeatured]                       = useState(null);
    const [featuredEditData, setFeaturedEditData]                       = useState({ id: "", name: "", description: "", sequenceNumber: "", validityStartDate: "", validityEndDate: "" });
    const [featuredEditProducts, setFeaturedEditProducts]               = useState([]);
    const [isFeaturedEditLoading, setIsFeaturedEditLoading]             = useState(false);
    const [isFeaturedDeleteLoading, setIsFeaturedDeleteLoading]         = useState(false);
    const [isFeaturedImgLoading, setIsFeaturedImgLoading]               = useState(false);
    const [featuredImage, setFeaturedImage]                             = useState(null);
    const [featuredBannerImage, setFeaturedBannerImage]                 = useState(null);
    const [searchText, setSearchText] = useState('');
    const [image, setImage] = useState(null);
    const [bannerImage, setBannerImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditLoading, setIsEditLoading] = useState(false);
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);
    const [isDownloadLoading, setIsDownloadLoading] = useState(false);
    const [imageId, setImageId] = useState("");
    const [categoryId, setCategoryId] = useState("");
    const animatedComponents = makeAnimated();
    const [outlets, setOutlets] = useState([]);
    const [selectedExcludedOutlets, setSelectedExcludedOutlets] = useState([]);
    const [editedSelectedExOutlets, setEditedSelectedExOutlets] = useState([]);
    const [openAddModal, setOpenAddModal] = useState(false);
    const [openEditModal, setOpenEditModal] = useState(false);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [editedData, setEditedData] = useState({});
    const [filteredData, setFilteredData] = useState([]);
    const [categoryList, setCategoryList] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showBannerImageModal, setShowBannerImageModal] = useState(false);
    const [openImageUploadModal, setOpenImageUploadModal] = useState(false);
    const [openBannerImageUploadModal, setOpenBannerImageUploadModal] = useState(false);
    const [isImageEditBtnLoading, setIsImageEditBtnLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage] = useState(10);
    const imageInputRef = useRef(null);

    const outletData = outlets.map((outlet) => ({ value: outlet._id, label: `${outlet.name} (${outlet.city})` }));

    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex   = startIndex + rowsPerPage;
    const paginatedData = filteredData.slice(startIndex, endIndex);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    const handlePageChange = (p) => setCurrentPage(p);

    // ── API calls (unchanged logic) ────────────────────────────────────────────
    const getOutlets = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("outlet", { headers });
            if (response?.data?.status === true) setOutlets(response?.data?.data?.outlets);
        } catch (error) {
            if (error.response && error.response?.data?.status === false)
                toast.error(error.response.data.error, { position: "top-right", autoClose: 3000, theme: "colored" });
        }
    };

    const getCategoryList = async (status) => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("masters/category/get", { headers });
            if (response?.data?.status === true) {
                response?.data?.data?.categories.forEach((item, index) => { item.serialNumber = index + 1; });
                setCategoryList(response?.data?.data?.categories);
                const filtered = response?.data?.data?.categories?.filter((cat) => cat?.status === status);
                if (status === "all" || status === undefined) setFilteredData(response?.data?.data?.categories);
                else setFilteredData(filtered);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false)
                toast.error(error.response.data.error, { position: "top-right", autoClose: 3000, theme: "colored" });
        }
    };

    const downloadCategoryReport = async () => {
        setIsDownloadLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("masters/category/get", { headers, params: { isDownload: true }, responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url  = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href  = url;
            link.setAttribute('download', `Category List.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
            toast.success("Report downloaded.", { position: "top-right", autoClose: 3000, theme: "colored" });
        } catch (error) {
            const msg = error.response?.data?.error || "Failed to download report";
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
        } finally { setIsDownloadLoading(false); }
    };

    const handleStatusFilter     = (e) => { setSelectedStatus(e.target.value); getCategoryList(e.target.value); };
    const handleSelectChange      = (s) => setSelectedExcludedOutlets(s);
    const handleEditSelectChange  = (s) => setEditedSelectedExOutlets(s);

    const handleOpenAddModal  = () => setOpenAddModal(true);
    const handleCloseAddModal = () => {
        setOpenAddModal(false); setBannerImage(null); setImage(null);
        setInputData({ name: '', description: '', isEventCategory: '', sequenceNumber: '' });
        setSelectedExcludedOutlets([]);
        setAddCategoryType('normal');
        setFeaturedData({ name: "", description: "", sequenceNumber: "", validityStartDate: "", validityEndDate: "" });
        setFeaturedProducts([{ product: "", sequenceNumber: 1, excludedOutlets: [], includedOutlets: [] }]);
    };

    const handleOpenEditModal = (data) => {
        setEditedData({ id: data._id, name: data?.name, sequenceNumber: data?.sequenceNumber, description: data?.description, isEventCategory: data?.isEventCategory, status: data?.status, orderType: data?.orderType });
        setEditedSelectedExOutlets(data?.excludedOutletDetails?.map((m) => ({ value: m?._id, label: m?.name })));
        setOpenEditModal(true);
    };
    const handleCloseEditModal    = () => setOpenEditModal(false);
    const handleCloseImageModal   = () => setShowImageModal(false);

    const handleImageUploadModal  = (data) => { setImage(data?.displayImage); setImageId(data?._id); setOpenImageUploadModal(true); setShowImageModal(false); };
    const handleOpenDeleteModal   = (data) => { setOpenDeleteModal(true); setCategoryId(data?._id); };
    const handleBannerImageUpload = (data) => { setOpenBannerImageUploadModal(true); setBannerImage(data?.bannerImage); setImageId(data?._id); setShowBannerImageModal(false); };
    const handleCloseDeleteModal  = () => setOpenDeleteModal(false);

    const handleChange = (e) => { const { name, value } = e.target; setInputData((p) => ({ ...p, [name]: value })); };

    const handleImageChange = (e) => {
        const allowed = ["png", "jpg", "jpeg"];
        const file    = e.target.files[0];
        if (file) {
            const ext = file.name.split(".").pop().toLowerCase();
            if (!allowed.includes(ext)) { toast.error("Invalid file format. Upload only jpg or png.", { position: "top-right", autoClose: 3000, theme: "colored" }); return; }
            setImage(file);
        }
    };

    const handleBannerImageChange = () => {
        const input = document.getElementById("imageInput");
        const file  = input.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.src = reader.result;
                img.onload = () => {
                    if (img.width === 1000 && img.height === 200) setBannerImage(file);
                    else { alert("Image dimensions must be 1000×200 px"); input.value = ""; }
                };
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBannerImageClick = (row) => { setSelectedImage(row); setShowBannerImageModal(true); };
    const handleImageClick        = (row) => { setSelectedImage(row); setShowImageModal(true); };

    const handleEditBannerImage = () => {
        const file = imageInputRef?.current?.files[0];
        if (file) { const reader = new FileReader(); setBannerImage(file); reader.readAsDataURL(file); }
    };

    const handleEditImage = async (e) => {
        e.preventDefault(); setIsImageEditBtnLoading(true);
        try {
            const formData = new FormData();
            formData.append("image", image); formData.append("id", imageId);
            const headers  = { "Content-Type": "multipart/form-data", Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.put("masters/category/update-images", formData, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message, { position: "top-right", autoClose: 3000, theme: "colored" });
                getCategoryList(); setOpenImageUploadModal(false);
            }
        } catch (error) {
            const msg = error.response?.data?.data?.message || error.response?.data?.error;
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
        } finally { setIsImageEditBtnLoading(false); }
    };

    const handleBannerEditImage = async (e) => {
        e.preventDefault(); setIsImageEditBtnLoading(true);
        try {
            const formData = new FormData();
            formData.append("bannerImage", bannerImage); formData.append("id", imageId);
            const headers  = { "Content-Type": "multipart/form-data", Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.put("masters/category/update-images", formData, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message, { position: "top-right", autoClose: 3000, theme: "colored" });
                getCategoryList(); setOpenBannerImageUploadModal(false);
            }
        } catch (error) {
            const msg = error.response?.data?.data?.message || error.response?.data?.error;
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
        } finally { setIsImageEditBtnLoading(false); }
    };

    const handleSearchChange = (e) => setSearchText(e.target.value);

    const addCategory = async (e) => {
        e.preventDefault(); setIsLoading(true);
        try {
            const selectOutlets = selectedExcludedOutlets.map((v) => v?.value);
            const formData = new FormData();
            formData.append("sequenceNumber", inputData.sequenceNumber);
            formData.append("name", inputData.name);
            formData.append("isEventCategory", inputData.isEventCategory);
            formData.append("description", inputData.description);
            formData.append("orderType", inputData.orderType);
            formData.append("image", image);
            formData.append("bannerImage", bannerImage);
            if (selectOutlets?.length > 0) selectOutlets.forEach((o, i) => formData.append(`excludedOutlets[${i}]`, o));
            const headers  = { "Content-Type": "multipart/form-data", Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.post("masters/category/add", formData, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message, { position: "top-right", autoClose: 3000, theme: "colored" });
                getCategoryList(); setSelectedExcludedOutlets([]);
                setInputData({ name: "", description: "", isEventCategory: "", sequenceNumber: "", orderType: "ALL" });
                setImage(null); setBannerImage(null); setOpenAddModal(false);
            }
        } catch (error) {
            if (error?.response) {
                let msg = '';
                if      (error.response?.status === 406) msg = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 412) msg = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) msg = error?.response?.data?.error;
                else if (error.response?.status === 404) msg = error?.response?.data?.error;
                toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
            }
        } finally { setIsLoading(false); }
    };

    // ── Featured category handlers ─────────────────────────────────────────────
    const handleFeaturedChange = (e) => {
        const { name, value } = e.target;
        setFeaturedData((p) => ({ ...p, [name]: value }));
    };

    const handleProductRowChange = (index, field, value) => {
        setFeaturedProducts((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleProductOutletChange = (index, field, selected) => {
        setFeaturedProducts((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: selected.map((s) => s.value) };
            return updated;
        });
    };

    const addProductRow = () => {
        setFeaturedProducts((prev) => [...prev, { product: "", sequenceNumber: prev.length + 1, excludedOutlets: [], includedOutlets: [] }]);
    };

    const removeProductRow = (index) => {
        setFeaturedProducts((prev) => prev.filter((_, i) => i !== index));
    };

    const addFeaturedCategory = async (e) => {
        e.preventDefault();
        setIsFeaturedLoading(true);
        try {
            const formData = new FormData();
            formData.append("name", featuredData.name);
            formData.append("description", featuredData.description);
            formData.append("sequenceNumber", featuredData.sequenceNumber);
            formData.append("validityStartDate", featuredData.validityStartDate);
            formData.append("validityEndDate", featuredData.validityEndDate);
            formData.append("products", JSON.stringify(featuredProducts));
            if (image)       formData.append("image", image);
            if (bannerImage) formData.append("bannerImage", bannerImage);

            const headers  = { "Content-Type": "multipart/form-data", Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.post("featured-category", formData, { headers });

            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message || "Featured category created!", { position: "top-right", autoClose: 3000, theme: "colored" });
                setFeaturedData({ name: "", description: "", sequenceNumber: "", validityStartDate: "", validityEndDate: "" });
                setFeaturedProducts([{ product: "", sequenceNumber: 1, excludedOutlets: [], includedOutlets: [] }]);
                setImage(null); setBannerImage(null);
                setOpenAddModal(false);
                getFeaturedCategoryList();
            }
        } catch (error) {
            if (error?.response) {
                let msg = error?.response?.data?.error?.message || error?.response?.data?.error || "Failed to create featured category";
                toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
            }
        } finally { setIsFeaturedLoading(false); }
    };

    // ── Open featured edit modal ───────────────────────────────────────────────
    const handleOpenFeaturedEditModal = (fc) => {
        setFeaturedEditData({
            id:                fc._id,
            name:              fc.name,
            description:       fc.description,
            sequenceNumber:    fc.sequenceNumber,
            validityStartDate: fc.validityStartDate ? fc.validityStartDate.split('T')[0] : "",
            validityEndDate:   fc.validityEndDate   ? fc.validityEndDate.split('T')[0]   : "",
        });
        setFeaturedEditProducts(
            fc.products?.map((p) => ({
                product:         p.product,
                sequenceNumber:  p.sequenceNumber,
                excludedOutlets: p.excludedOutlets || [],
                includedOutlets: p.includedOutlets || [],
            })) || []
        );
        setOpenFeaturedEditModal(true);
    };

    const handleFeaturedEditChange = (e) => {
        const { name, value } = e.target;
        setFeaturedEditData((p) => ({ ...p, [name]: value }));
    };

    const handleEditProductRowChange = (index, field, value) => {
        setFeaturedEditProducts((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handleEditProductOutletChange = (index, field, selected) => {
        setFeaturedEditProducts((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: selected.map((s) => s.value) };
            return updated;
        });
    };

    const addEditProductRow = () => {
        setFeaturedEditProducts((prev) => [...prev, { product: "", sequenceNumber: prev.length + 1, excludedOutlets: [], includedOutlets: [] }]);
    };

    const removeEditProductRow = (index) => {
        setFeaturedEditProducts((prev) => prev.filter((_, i) => i !== index));
    };

    // PUT /featured-category  (update details)
    const updateFeaturedCategory = async (e) => {
        e.preventDefault();
        setIsFeaturedEditLoading(true);
        try {
            const formData = new FormData();
            formData.append("id",                featuredEditData.id);
            formData.append("name",              featuredEditData.name);
            formData.append("description",       featuredEditData.description);
            formData.append("sequenceNumber",    featuredEditData.sequenceNumber);
            formData.append("validityStartDate", featuredEditData.validityStartDate);
            formData.append("validityEndDate",   featuredEditData.validityEndDate);
            formData.append("products",          JSON.stringify(featuredEditProducts));
            const headers  = { "Content-Type": "multipart/form-data", Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.put("featured-category", formData, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message || "Updated successfully!", { position: "top-right", autoClose: 3000, theme: "colored" });
                setOpenFeaturedEditModal(false);
                getFeaturedCategoryList();
            }
        } catch (error) {
            const msg = error?.response?.data?.error?.message || error?.response?.data?.error || "Update failed";
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
        } finally { setIsFeaturedEditLoading(false); }
    };

    // DELETE /featured-category  (body: { _id })
    const deleteFeaturedCategory = async (e) => {
        e.preventDefault();
        setIsFeaturedDeleteLoading(true);
        try {
            const headers  = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.delete("featured-category", { headers, data: { _id: selectedFeatured?._id } });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message || "Deleted successfully!", { position: "top-right", autoClose: 3000, theme: "colored" });
                setOpenFeaturedDeleteModal(false);
                setSelectedFeatured(null);
                getFeaturedCategoryList();
            }
        } catch (error) {
            const msg = error?.response?.data?.error?.message || error?.response?.data?.error || "Delete failed";
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
        } finally { setIsFeaturedDeleteLoading(false); }
    };

    // PUT /featured-category/update-images  (image only)
    const updateFeaturedImage = async (e) => {
        e.preventDefault();
        setIsFeaturedImgLoading(true);
        try {
            const formData = new FormData();
            formData.append("id",    selectedFeatured?._id);
            formData.append("image", featuredImage);
            const headers  = { "Content-Type": "multipart/form-data", Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.put("featured-category/update-images", formData, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message || "Image updated!", { position: "top-right", autoClose: 3000, theme: "colored" });
                setOpenFeaturedImgModal(false);
                setFeaturedImage(null);
                getFeaturedCategoryList();
            }
        } catch (error) {
            const msg = error?.response?.data?.error?.message || error?.response?.data?.error || "Image update failed";
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
        } finally { setIsFeaturedImgLoading(false); }
    };

    // PUT /featured-category/update-images  (bannerImage only)
    const updateFeaturedBannerImage = async (e) => {
        e.preventDefault();
        setIsFeaturedImgLoading(true);
        try {
            const formData = new FormData();
            formData.append("id",          selectedFeatured?._id);
            formData.append("bannerImage", featuredBannerImage);
            const headers  = { "Content-Type": "multipart/form-data", Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.put("featured-category/update-images", formData, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message || "Banner updated!", { position: "top-right", autoClose: 3000, theme: "colored" });
                setOpenFeaturedBannerModal(false);
                setFeaturedBannerImage(null);
                getFeaturedCategoryList();
            }
        } catch (error) {
            const msg = error?.response?.data?.error?.message || error?.response?.data?.error || "Banner update failed";
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
        } finally { setIsFeaturedImgLoading(false); }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        const updated = name === "isComboCategory" ? value === "true" : value;
        setEditedData((p) => ({ ...p, [name]: updated }));
    };

    const editCategory = async (e) => {
        e.preventDefault(); setIsEditLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const newData = { ...editedData, excludedOutlets: editedSelectedExOutlets?.map((ex) => ex?.value) };
            const response = await axiosInstance.put("masters/category/update", newData, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message, { position: "top-right", autoClose: 3000, theme: "colored" });
                getCategoryList(); setOpenEditModal(false);
            }
        } catch (error) {
            if (error?.response) {
                let msg = '';
                if      (error.response?.status === 406) msg = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 403) msg = error?.response?.data?.data?.message;
                else if (error.response?.status === 412) msg = error?.response?.data?.data?.message;
                else if (error.response?.status === 500) msg = error?.response?.data?.error;
                toast.error(msg, { position: "top-right", autoClose: 3000, theme: "colored" });
            }
        } finally { setIsEditLoading(false); }
    };

    const deleteCategory = async (e) => {
        e.preventDefault(); setIsDeleteLoading(true);
        try {
            const headers  = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.delete(`masters/category/delete?id=${categoryId}`, { headers });
            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message, { position: "top-right", autoClose: 3000, theme: "colored" });
                getCategoryList(); setOpenDeleteModal(false);
            }
        } catch (error) {
            let msg = '';
            if      (error.response?.status === 406) msg = error?.response?.data?.error?.message || error?.response?.data?.error;
            else if (error.response?.status === 403) msg = error?.response?.data?.message;
            else if (error.response?.status === 412) msg = error?.response?.data?.message;
            else if (error.response?.status === 500) msg = error?.response?.data?.error;
            toast.error(msg, { position: "top-right", autoClose: 3000, theme: "light" });
        } finally { setIsDeleteLoading(false); }
    };

    const getProductsList = async () => {
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("product", { headers });
            if (response?.data?.status === true) {
                setFilteredProductList(response?.data?.data?.products);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                toast.error(error.response.data.error, { position: "top-right", autoClose: 3000, theme: "colored" });
            }
        }
    };

    const getFeaturedCategoryList = async () => {
        setIsFeaturedListLoading(true);
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const response = await axiosInstance.get("featured-category", { headers });
            if (response?.data?.status === true) {
                setFeaturedCategoryList(response?.data?.data?.featuredCategories || []);
            }
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                toast.error(error.response.data.error, { position: "top-right", autoClose: 3000, theme: "colored" });
            }
        } finally {
            setIsFeaturedListLoading(false);
        }
    };

    useEffect(() => { getOutlets(); getCategoryList(); getProductsList(); getFeaturedCategoryList(); }, []);
    useEffect(() => {
        const result = categoryList.filter((item) =>
            item.name.toLowerCase().includes(searchText.toLowerCase()) ||
            item.description.toLowerCase().includes(searchText.toLowerCase())
        );
        setFilteredData(result);
    }, [searchText]);

    // ─── Render ────────────────────────────────────────────────────────────────
    return (
        <React.Fragment>
            <Header onSkin={setSkin} />

            <div className="main main-app p-3 p-lg-4" style={{ background: T.bg, minHeight: '100vh' }}>
                <div style={{ background: T.surface, borderRadius: '6px', border: `0.5px solid ${T.border}`, padding: '20px 24px' }}>

                    {/* ── Page title + Tab switcher ── */}
                    <div style={{ marginBottom: '20px', paddingBottom: '0', borderBottom: `0.5px solid ${T.border}`, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', background: T.blue, margin: '-20px -24px 20px', padding: '16px 24px 0', borderRadius: '6px 6px 0 0' }}>
                        <div style={{ paddingBottom: '0' }}>
                            <p style={{ margin: 0, fontSize: '11px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '1px', color: 'rgba(255,255,255,0.6)' }}>Menu</p>
                            <h5 style={{ margin: '2px 0 14px', fontSize: '16px', fontWeight: '500', color: '#ffffff' }}>Category Management</h5>
                        </div>
                        {/* Tab strip */}
                        <div style={{ display: 'flex', gap: '0', marginBottom: '-1px' }}>
                            {[
                                { key: 'normal',   label: 'Normal Categories' },
                                { key: 'featured', label: 'Featured Categories' },
                            ].map(({ key, label }) => (
                                <button
                                    key={key}
                                    onClick={() => setActiveTab(key)}
                                    style={{
                                        height: '34px', padding: '0 18px',
                                        border: 'none',
                                        borderBottom: activeTab === key ? `2px solid #ffffff` : '2px solid transparent',
                                        background: 'transparent',
                                        color: activeTab === key ? '#ffffff' : 'rgba(255,255,255,0.55)',
                                        fontSize: '13px', fontWeight: activeTab === key ? '600' : '400',
                                        cursor: 'pointer', fontFamily: 'inherit',
                                        transition: 'color 0.15s, border-color 0.15s',
                                    }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ══════════════════════════════════════════════
                        TAB: NORMAL CATEGORIES
                    ══════════════════════════════════════════════ */}
                    {activeTab === 'normal' && (
                        <>
                            {/* Controls bar */}
                            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <label style={labelStyle}>Status</label>
                                    <select style={{ ...inputStyle, width: '140px' }} value={selectedStatus} onChange={handleStatusFilter}>
                                        <option value="all">All</option>
                                        <option value="ACTIVE">Active</option>
                                        <option value="INACTIVE">Inactive</option>
                                    </select>
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '200px' }}>
                                    <label style={labelStyle}>Search</label>
                                    <div style={{ position: 'relative' }}>
                                        <i className="ri-search-line" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', color: T.textFaint, fontSize: '14px' }} />
                                        <input type="text" style={{ ...inputStyle, paddingLeft: '30px' }} value={searchText} onChange={handleSearchChange} placeholder="Search categories…" />
                                    </div>
                                </div>

                                <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                                    <button style={ghostBtn} onClick={downloadCategoryReport} disabled={isDownloadLoading}>
                                        {isDownloadLoading
                                            ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Downloading…</>
                                            : <><i className="ri-download-line" /> Export</>
                                        }
                                    </button>
                                    {(menuAccess.includes("CategoryMgt:add") || email === "contact@xpansecafe.com") && (
                                        <button style={primaryBtn} onClick={handleOpenAddModal}>
                                            <i className="ri-add-line" /> Add Category
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Normal categories table */}
                            {(menuAccess.includes("CategoryMgt:get") || email === "contact@xpansecafe.com") && (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ borderBottom: `0.5px solid ${T.borderMid}` }}>
                                                {['#', 'Banner', 'Image', 'Seq', 'Name', 'Status', 'Description',
                                                  (menuAccess.includes("CategoryMgt:update") || menuAccess.includes("CategoryMgt:delete") || email === "contact@xpansecafe.com") ? 'Actions' : null
                                                ].filter(Boolean).map((h) => (
                                                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '500', color: T.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {paginatedData?.map((cat) => (
                                                <tr
                                                    key={cat._id}
                                                    style={{ borderBottom: `0.5px solid ${T.border}`, verticalAlign: 'middle' }}
                                                    onMouseEnter={(e) => Array.from(e.currentTarget.cells).forEach(td => td.style.background = T.bg)}
                                                    onMouseLeave={(e) => Array.from(e.currentTarget.cells).forEach(td => td.style.background = 'transparent')}
                                                >
                                                    <td style={{ padding: '12px 14px', color: T.textFaint, fontSize: '12px' }}>{cat?.serialNumber ?? '—'}</td>
                                                    <td style={{ padding: '12px 14px' }}>
                                                        <img src={cat?.bannerImage} alt="Banner" onClick={() => handleBannerImageClick(cat)} style={{ width: '120px', height: '36px', objectFit: 'cover', borderRadius: '3px', border: `0.5px solid ${T.border}`, cursor: 'pointer', display: 'block' }} />
                                                    </td>
                                                    <td style={{ padding: '12px 14px' }}>
                                                        <img src={cat?.displayImage} alt="Category" onClick={() => handleImageClick(cat)} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', border: `0.5px solid ${T.border}`, cursor: 'pointer', display: 'block' }} />
                                                    </td>
                                                    <td style={{ padding: '12px 14px', color: T.textFaint, fontVariantNumeric: 'tabular-nums', fontSize: '12px' }}>{cat?.sequenceNumber}</td>
                                                    <td style={{ padding: '12px 14px', minWidth: '140px' }}>
                                                        <div style={{ fontWeight: '500', color: T.textPrimary }}>{cat?.name}</div>
                                                        <div style={{ fontSize: '11px', color: cat.isEventCategory ? T.amber : T.textFaint, marginTop: '2px' }}>{cat?.isEventCategory === true ? 'Event outlet' : 'Regular outlet'}</div>
                                                    </td>
                                                    <td style={{ padding: '12px 14px', minWidth: '100px' }}>
                                                        {cat?.status && <StatusDot status={cat.status} />}
                                                        <div style={{ fontSize: '11px', marginTop: '4px', fontWeight: '500', color: orderColor(cat?.orderType) }}>{cat?.orderType || 'ALL'}</div>
                                                    </td>
                                                    <td style={{ padding: '12px 14px', maxWidth: '220px' }}>
                                                        {cat?.excludedOutletDetails?.length > 0 ? (
                                                            <div style={{ fontSize: '11px', fontWeight: '500', color: T.purple, marginBottom: '3px' }}>
                                                                {cat.excludedOutletDetails.map(o => o?.name?.toLowerCase().trim()).join(', ')}
                                                            </div>
                                                        ) : (
                                                            <div style={{ fontSize: '11px', color: T.textFaint, marginBottom: '3px' }}>No outlets excluded</div>
                                                        )}
                                                        <div style={{ fontSize: '12px', color: T.textMuted, lineHeight: '1.5', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{cat?.description}</div>
                                                    </td>
                                                    {((menuAccess.includes("CategoryMgt:update") || menuAccess.includes("CategoryMgt:delete")) || email === "contact@xpansecafe.com") && (
                                                        <td style={{ padding: '12px 14px' }}>
                                                            <Dropdown>
                                                                <Dropdown.Toggle size="sm" id={`dd-${cat._id}`} style={{ ...ghostBtn, height: '28px', padding: '0 10px', fontSize: '12px' }} bsPrefix="no-caret">
                                                                    <i className="ri-more-2-fill" /> Actions
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu style={{ border: `0.5px solid ${T.border}`, borderRadius: '4px', fontSize: '13px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', padding: '4px 0', minWidth: '120px' }}>
                                                                    {(menuAccess.includes("CategoryMgt:update") || email === "contact@xpansecafe.com") && (
                                                                        <Dropdown.Item onClick={() => handleOpenEditModal(cat)} style={{ padding: '7px 14px', color: T.blue, fontSize: '13px' }}>
                                                                            <i className="ri-edit-line" style={{ marginRight: '6px' }} />Edit
                                                                        </Dropdown.Item>
                                                                    )}
                                                                    {(menuAccess.includes("CategoryMgt:delete") || email === "contact@xpansecafe.com") && (
                                                                        <Dropdown.Item onClick={() => handleOpenDeleteModal(cat)} style={{ padding: '7px 14px', color: T.red, fontSize: '13px' }}>
                                                                            <i className="ri-delete-bin-line" style={{ marginRight: '6px' }} />Delete
                                                                        </Dropdown.Item>
                                                                    )}
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Normal pagination */}
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '4px', marginTop: '20px' }}>
                                {[...Array(totalPages).keys()].map((i) => (
                                    <button key={i + 1} onClick={() => handlePageChange(i + 1)}
                                        style={{ width: '30px', height: '30px', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', border: `0.5px solid ${i + 1 === currentPage ? T.blue : T.border}`, background: i + 1 === currentPage ? T.blue : T.surface, color: i + 1 === currentPage ? '#fff' : T.textMuted, fontFamily: 'inherit' }}>
                                        {i + 1}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}

                    {/* ══════════════════════════════════════════════
                        TAB: FEATURED CATEGORIES
                    ══════════════════════════════════════════════ */}
                    {activeTab === 'featured' && (
                        <>
                            {/* Featured controls bar */}
                            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', gap: '8px', marginBottom: '20px' }}>
                                <button style={ghostBtn} onClick={getFeaturedCategoryList} disabled={isFeaturedListLoading}>
                                    {isFeaturedListLoading
                                        ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Refreshing…</>
                                        : <><i className="ri-refresh-line" /> Refresh</>
                                    }
                                </button>
                                {(menuAccess.includes("CategoryMgt:add") || email === "contact@xpansecafe.com") && (
                                    <button style={{ ...primaryBtn, background: T.amber, borderColor: T.amber }} onClick={() => { setAddCategoryType('featured'); setOpenAddModal(true); }}>
                                        <i className="ri-star-line" /> Add Featured
                                    </button>
                                )}
                            </div>

                            {/* Featured categories table */}
                            {isFeaturedListLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: T.textFaint }}>
                                    <Spinner animation="border" size="sm" /> <span style={{ marginLeft: '8px', fontSize: '13px' }}>Loading…</span>
                                </div>
                            ) : featuredCategoryList.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '48px 0', color: T.textFaint, fontSize: '13px' }}>
                                    <i className="ri-star-line" style={{ fontSize: '28px', display: 'block', marginBottom: '8px', opacity: 0.4 }} />
                                    No featured categories yet
                                </div>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                        <thead>
                                            <tr style={{ borderBottom: `0.5px solid ${T.borderMid}` }}>
                                                {['Banner', 'Image', 'Seq', 'Name', 'Validity', 'Status', 'Products', 'Actions'].map((h) => (
                                                    <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '500', color: T.textFaint, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                                                        {h}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {featuredCategoryList.map((fc) => {
                                                const startDate = fc.validityStartDate ? new Date(fc.validityStartDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                                                const endDate   = fc.validityEndDate   ? new Date(fc.validityEndDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })   : '—';
                                                const now       = new Date();
                                                const isLive    = fc.status === 'ACTIVE' && new Date(fc.validityStartDate) <= now && new Date(fc.validityEndDate) >= now;
                                                return (
                                                    <tr key={fc._id}
                                                        style={{ borderBottom: `0.5px solid ${T.border}`, verticalAlign: 'middle' }}
                                                        onMouseEnter={(e) => Array.from(e.currentTarget.cells).forEach(td => td.style.background = T.bg)}
                                                        onMouseLeave={(e) => Array.from(e.currentTarget.cells).forEach(td => td.style.background = 'transparent')}
                                                    >
                                                        {/* Banner */}
                                                        <td style={{ padding: '12px 14px' }}>
                                                            <img src={fc.bannerImage} alt="Banner" onClick={() => { setSelectedFeatured(fc); setOpenFeaturedBannerModal(true); }} style={{ width: '120px', height: '36px', objectFit: 'cover', borderRadius: '3px', border: `0.5px solid ${T.border}`, display: 'block', cursor: 'pointer' }} />
                                                        </td>
                                                        {/* Image */}
                                                        <td style={{ padding: '12px 14px' }}>
                                                            <img src={fc.displayImage} alt={fc.name} onClick={() => { setSelectedFeatured(fc); setOpenFeaturedImgModal(true); }} style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', border: `0.5px solid ${T.border}`, display: 'block', cursor: 'pointer' }} />
                                                        </td>
                                                        {/* Seq */}
                                                        <td style={{ padding: '12px 14px', color: T.textFaint, fontSize: '12px', fontVariantNumeric: 'tabular-nums' }}>
                                                            {fc.sequenceNumber}
                                                        </td>
                                                        {/* Name + description */}
                                                        <td style={{ padding: '12px 14px', minWidth: '160px' }}>
                                                            <div style={{ fontWeight: '500', color: T.textPrimary }}>{fc.name}</div>
                                                            <div style={{ fontSize: '11px', color: T.textFaint, marginTop: '2px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{fc.description}</div>
                                                        </td>
                                                        {/* Validity */}
                                                        <td style={{ padding: '12px 14px', minWidth: '160px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: T.textMuted }}>
                                                                <i className="ri-calendar-line" style={{ color: T.textFaint }} />
                                                                {startDate}
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', color: T.textMuted, marginTop: '3px' }}>
                                                                <i className="ri-calendar-check-line" style={{ color: T.textFaint }} />
                                                                {endDate}
                                                            </div>
                                                            {isLive && (
                                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginTop: '4px', fontSize: '10px', fontWeight: '600', color: T.green, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: T.green }} />Live
                                                                </span>
                                                            )}
                                                        </td>
                                                        {/* Status */}
                                                        <td style={{ padding: '12px 14px' }}>
                                                            <StatusDot status={fc.status} />
                                                        </td>
                                                        {/* Products */}
                                                        <td style={{ padding: '12px 14px', minWidth: '200px' }}>
                                                            {fc.products?.length > 0 ? (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                    {fc.products.map((p, pi) => (
                                                                        <div key={p._id} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '6px 8px', border: `0.5px solid ${T.border}`, borderRadius: '4px', background: T.bg }}>
                                                                            <span style={{ fontSize: '10px', fontWeight: '600', color: T.textFaint, minWidth: '14px', marginTop: '1px' }}>#{p.sequenceNumber}</span>
                                                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                                                <div style={{ fontSize: '12px', fontWeight: '500', color: T.textPrimary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                                                    {p.productDetail?.name || '—'}
                                                                                </div>
                                                                                {p.excludedOutletDetails?.length > 0 && (
                                                                                    <div style={{ fontSize: '10px', color: T.red, marginTop: '2px' }}>
                                                                                        excl: {p.excludedOutletDetails.map(o => o.name).join(', ')}
                                                                                    </div>
                                                                                )}
                                                                                {p.includedOutletDetails?.length > 0 && (
                                                                                    <div style={{ fontSize: '10px', color: T.green, marginTop: '2px' }}>
                                                                                        incl: {p.includedOutletDetails.map(o => o.name).join(', ')}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span style={{ fontSize: '12px', color: T.textFaint }}>No products</span>
                                                            )}
                                                        </td>
                                                        {/* Actions */}
                                                        <td style={{ padding: '12px 14px' }}>
                                                            <Dropdown>
                                                                <Dropdown.Toggle size="sm" id={`fdd-${fc._id}`} style={{ ...ghostBtn, height: '28px', padding: '0 10px', fontSize: '12px' }} bsPrefix="no-caret">
                                                                    <i className="ri-more-2-fill" /> Actions
                                                                </Dropdown.Toggle>
                                                                <Dropdown.Menu style={{ border: `0.5px solid ${T.border}`, borderRadius: '4px', fontSize: '13px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', padding: '4px 0', minWidth: '140px' }}>
                                                                    <Dropdown.Item onClick={() => handleOpenFeaturedEditModal(fc)} style={{ padding: '7px 14px', color: T.blue, fontSize: '13px' }}>
                                                                        <i className="ri-edit-line" style={{ marginRight: '6px' }} />Edit Details
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item onClick={() => { setSelectedFeatured(fc); setOpenFeaturedImgModal(true); }} style={{ padding: '7px 14px', color: T.textMuted, fontSize: '13px' }}>
                                                                        <i className="ri-image-edit-line" style={{ marginRight: '6px' }} />Update Image
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Item onClick={() => { setSelectedFeatured(fc); setOpenFeaturedBannerModal(true); }} style={{ padding: '7px 14px', color: T.textMuted, fontSize: '13px' }}>
                                                                        <i className="ri-landscape-line" style={{ marginRight: '6px' }} />Update Banner
                                                                    </Dropdown.Item>
                                                                    <Dropdown.Divider style={{ margin: '4px 0', borderColor: T.border }} />
                                                                    <Dropdown.Item onClick={() => { setSelectedFeatured(fc); setOpenFeaturedDeleteModal(true); }} style={{ padding: '7px 14px', color: T.red, fontSize: '13px' }}>
                                                                        <i className="ri-delete-bin-line" style={{ marginRight: '6px' }} />Delete
                                                                    </Dropdown.Item>
                                                                </Dropdown.Menu>
                                                            </Dropdown>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ── ADD MODAL ── */}
                <Modal show={openAddModal} onHide={handleCloseAddModal} size="lg" centered>
                    <Modal.Header closeButton style={modalHeaderStyle}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%' }}>
                            <span style={{ fontSize: '15px', fontWeight: '500', color: T.textPrimary }}>Add New Category</span>
                            {/* ── Type switcher ── */}
                            <div style={{ display: 'inline-flex', border: `0.5px solid ${T.borderMid}`, borderRadius: '4px', overflow: 'hidden', width: 'fit-content' }}>
                                {[
                                    { key: 'normal',   label: 'Normal Category' },
                                    { key: 'featured', label: 'Featured Category' },
                                ].map(({ key, label }) => (
                                    <button
                                        key={key}
                                        onClick={() => setAddCategoryType(key)}
                                        style={{
                                            height: '30px', padding: '0 16px', border: 'none',
                                            background: addCategoryType === key ? T.blue : T.surface,
                                            color:      addCategoryType === key ? '#fff'  : T.textMuted,
                                            fontSize: '12px', fontWeight: '500', cursor: 'pointer',
                                            fontFamily: 'inherit', transition: 'background 0.15s',
                                            borderRight: key === 'normal' ? `0.5px solid ${T.borderMid}` : 'none',
                                        }}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </Modal.Header>

                    {/* ── NORMAL CATEGORY FORM ── */}
                    {addCategoryType === 'normal' && (
                        <>
                            <Modal.Body style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                                    <Field label="Category Name" required>
                                        <input style={inputStyle} name="name" value={inputData.name} type="text" placeholder="e.g. Hot Classics" onChange={handleChange} />
                                    </Field>
                                    <Field label="Sequence No." required>
                                        <input style={inputStyle} name="sequenceNumber" value={inputData.sequenceNumber} type="number" placeholder="e.g. 100" onChange={handleChange} />
                                    </Field>
                                    <Field label="Outlet Type" required>
                                        <select style={inputStyle} name="isEventCategory" value={inputData.isEventCategory} onChange={handleChange}>
                                            <option value="">Select type</option>
                                            <option value="false">Regular outlet</option>
                                            <option value="true">Event outlet</option>
                                        </select>
                                    </Field>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '14px' }}>
                                    <Field label="Order Type" required>
                                        <select style={inputStyle} name="orderType" value={inputData.orderType} onChange={handleChange}>
                                            <option value="ALL">All</option>
                                            <option value="DELIVERY">Delivery</option>
                                            <option value="PICKUP">Pick-up</option>
                                        </select>
                                    </Field>
                                    <Field label="Excluded Outlets">
                                        <Select isMulti components={animatedComponents} value={selectedExcludedOutlets} onChange={handleSelectChange} options={outletData} styles={selectStyles} placeholder="Select outlets to exclude…" closeMenuOnSelect={false} />
                                    </Field>
                                </div>
                                <Field label="Description" required>
                                    <textarea style={{ ...inputStyle, height: '72px', padding: '8px 10px', resize: 'vertical' }} name="description" value={inputData.description} placeholder="Brief description of this category" onChange={handleChange} />
                                </Field>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                    <Field label="Banner Image (1000×200 px)" required>
                                        <input style={{ ...inputStyle, height: 'auto', padding: '4px 8px' }} type="file" id="imageInput" accept="image/*" onChange={handleBannerImageChange} />
                                        <span style={{ fontSize: '11px', color: T.red, marginTop: '2px' }}>Dimensions must be exactly 1000×200 px</span>
                                    </Field>
                                    <Field label="Category Image" required>
                                        <input style={{ ...inputStyle, height: 'auto', padding: '4px 8px' }} type="file" accept="image/*" onChange={handleImageChange} />
                                    </Field>
                                </div>
                            </Modal.Body>
                            <Modal.Footer style={modalFooterStyle}>
                                <button style={ghostBtn} onClick={handleCloseAddModal}>Cancel</button>
                                <button style={primaryBtn} onClick={addCategory}>
                                    {isLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Add Category'}
                                </button>
                            </Modal.Footer>
                        </>
                    )}

                    {/* ── FEATURED CATEGORY FORM ── */}
                    {addCategoryType === 'featured' && (
                        <>
                            <Modal.Body style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

                                {/* Basic info */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                                    <Field label="Collection Name" required>
                                        <input style={inputStyle} name="name" value={featuredData.name} type="text" placeholder="e.g. Summer Collection" onChange={handleFeaturedChange} />
                                    </Field>
                                    <Field label="Sequence No." required>
                                        <input style={inputStyle} name="sequenceNumber" value={featuredData.sequenceNumber} type="number" placeholder="e.g. 1" onChange={handleFeaturedChange} />
                                    </Field>
                                    <div /> {/* spacer */}
                                </div>

                                {/* Validity dates */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                    <Field label="Validity Start Date" required>
                                        <DatePicker
                                            selected={featuredData.validityStartDate ? new Date(featuredData.validityStartDate) : null}
                                            onChange={(date) => setFeaturedData((p) => ({ ...p, validityStartDate: date ? date.toISOString().split('T')[0] : "" }))}
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText="Select start date"
                                            minDate={new Date()}
                                            isClearable
                                            customInput={<input style={inputStyle} readOnly />}
                                            wrapperStyle={{ width: '100%' }}
                                            popperPlacement="bottom-start"
                                        />
                                    </Field>
                                    <Field label="Validity End Date" required>
                                        <DatePicker
                                            selected={featuredData.validityEndDate ? new Date(featuredData.validityEndDate) : null}
                                            onChange={(date) => setFeaturedData((p) => ({ ...p, validityEndDate: date ? date.toISOString().split('T')[0] : "" }))}
                                            dateFormat="yyyy-MM-dd"
                                            placeholderText="Select end date"
                                            minDate={featuredData.validityStartDate ? new Date(featuredData.validityStartDate) : new Date()}
                                            isClearable
                                            customInput={<input style={inputStyle} readOnly />}
                                            wrapperStyle={{ width: '100%' }}
                                            popperPlacement="bottom-start"
                                        />
                                    </Field>
                                </div>

                                <Field label="Description" required>
                                    <textarea style={{ ...inputStyle, height: '66px', padding: '8px 10px', resize: 'vertical' }} name="description" value={featuredData.description} placeholder="e.g. Featured products for summer season" onChange={handleFeaturedChange} />
                                </Field>

                                {/* Images */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                    <Field label="Banner Image (1000×200 px)" required>
                                        <input style={{ ...inputStyle, height: 'auto', padding: '4px 8px' }} type="file" id="imageInput" accept="image/*" onChange={handleBannerImageChange} />
                                        <span style={{ fontSize: '11px', color: T.red, marginTop: '2px' }}>Dimensions must be exactly 1000×200 px</span>
                                    </Field>
                                    <Field label="Category Image" required>
                                        <input style={{ ...inputStyle, height: 'auto', padding: '4px 8px' }} type="file" accept="image/*" onChange={handleImageChange} />
                                    </Field>
                                </div>

                                {/* Products section */}
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <label style={{ ...labelStyle, marginBottom: 0 }}>
                                            Products <span style={{ color: T.red }}>*</span>
                                        </label>
                                        <button style={{ ...ghostBtn, height: '26px', padding: '0 10px', fontSize: '12px' }} onClick={addProductRow}>
                                            <i className="ri-add-line" /> Add Product
                                        </button>
                                    </div>

                                    {/* Column headers */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 1fr 1fr 36px', gap: '8px', marginBottom: '6px', padding: '0 2px' }}>
                                        {['Product', 'Seq', 'Excluded Outlets', 'Included Outlets', ''].map((h) => (
                                            <span key={h} style={{ ...labelStyle, marginBottom: 0 }}>{h}</span>
                                        ))}
                                    </div>

                                    {/* Product rows */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {featuredProducts.map((row, idx) => (
                                            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 1fr 1fr 36px', gap: '8px', alignItems: 'center' }}>
                                                <Select
                                                    options={filteredProductList?.map((rm) => ({
                                                        value: rm?._id,
                                                        label: rm?.name,
                                                    }))}
                                                    value={
                                                        row.product
                                                            ? {
                                                                value: row.product,
                                                                label: filteredProductList?.find((rm) => rm._id === row.product)?.name || row.product,
                                                              }
                                                            : null
                                                    }
                                                    onChange={(sel) => handleProductRowChange(idx, 'product', sel ? sel.value : "")}
                                                    components={animatedComponents}
                                                    styles={{ ...selectStyles, control: (p, s) => ({ ...selectStyles.control(p, s), fontSize: '12px', minHeight: '34px' }) }}
                                                    placeholder="Select product…"
                                                    isClearable
                                                />
                                                <input
                                                    style={{ ...inputStyle, fontSize: '12px' }}
                                                    type="number"
                                                    value={row.sequenceNumber}
                                                    min={1}
                                                    onChange={(e) => handleProductRowChange(idx, 'sequenceNumber', Number(e.target.value))}
                                                />
                                                <Select
                                                    isMulti
                                                    components={animatedComponents}
                                                    value={outletData.filter(o => row.excludedOutlets.includes(o.value))}
                                                    onChange={(sel) => handleProductOutletChange(idx, 'excludedOutlets', sel)}
                                                    options={outletData}
                                                    styles={{ ...selectStyles, control: (p, s) => ({ ...selectStyles.control(p, s), fontSize: '12px', minHeight: '34px' }) }}
                                                    placeholder="Excluded…"
                                                    closeMenuOnSelect={false}
                                                />
                                                <Select
                                                    isMulti
                                                    components={animatedComponents}
                                                    value={outletData.filter(o => row.includedOutlets.includes(o.value))}
                                                    onChange={(sel) => handleProductOutletChange(idx, 'includedOutlets', sel)}
                                                    options={outletData}
                                                    styles={{ ...selectStyles, control: (p, s) => ({ ...selectStyles.control(p, s), fontSize: '12px', minHeight: '34px' }) }}
                                                    placeholder="Included…"
                                                    closeMenuOnSelect={false}
                                                />
                                                <button
                                                    onClick={() => removeProductRow(idx)}
                                                    disabled={featuredProducts.length === 1}
                                                    style={{ width: '36px', height: '34px', border: `0.5px solid ${T.borderMid}`, borderRadius: '4px', background: T.surface, cursor: featuredProducts.length === 1 ? 'not-allowed' : 'pointer', color: T.red, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: featuredProducts.length === 1 ? 0.4 : 1 }}
                                                >
                                                    <i className="ri-delete-bin-line" style={{ fontSize: '14px' }} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Modal.Body>

                            <Modal.Footer style={modalFooterStyle}>
                                <button style={ghostBtn} onClick={handleCloseAddModal}>Cancel</button>
                                <button
                                    style={{ ...primaryBtn, background: T.amber, borderColor: T.amber }}
                                    onClick={addFeaturedCategory}
                                >
                                    {isFeaturedLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : <><i className="ri-star-line" /> Create Featured</>}
                                </button>
                            </Modal.Footer>
                        </>
                    )}
                </Modal>

                {/* ── EDIT MODAL ── */}
                <Modal show={openEditModal} onHide={handleCloseEditModal} size="lg" centered>
                    <Modal.Header closeButton style={modalHeaderStyle}>
                        <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Update Category</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '14px' }}>
                            <Field label="Category Name" required>
                                <input style={inputStyle} name="name" value={editedData?.name} type="text" placeholder="Category name" onChange={handleEditChange} />
                            </Field>
                            <Field label="Seq No." required>
                                <input style={inputStyle} name="sequenceNumber" value={editedData?.sequenceNumber} type="number" onChange={handleEditChange} />
                            </Field>
                            <Field label="Outlet Type" required>
                                <select style={inputStyle} name="isEventCategory" value={editedData?.isEventCategory} onChange={handleEditChange}>
                                    <option value="">Select</option>
                                    <option value="false">Regular outlet</option>
                                    <option value="true">Event outlet</option>
                                </select>
                            </Field>
                            <Field label="Status" required>
                                <select style={inputStyle} name="status" value={editedData?.status} onChange={handleEditChange}>
                                    <option value="">Select status</option>
                                    <option value="ACTIVE">Active</option>
                                    <option value="INACTIVE">Inactive</option>
                                </select>
                            </Field>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: '14px' }}>
                            <Field label="Order Type" required>
                                <select style={inputStyle} name="orderType" value={editedData.orderType || ""} onChange={handleEditChange}>
                                    <option value="" disabled>Select</option>
                                    <option value="ALL">All</option>
                                    <option value="DELIVERY">Delivery</option>
                                    <option value="PICKUP">Pick-up</option>
                                </select>
                            </Field>
                            <Field label="Combo Category" required>
                                <select style={inputStyle} name="isComboCategory" value={editedData?.isComboCategory} onChange={handleEditChange}>
                                    <option value="">Select</option>
                                    <option value="true">Yes</option>
                                    <option value="false">No</option>
                                </select>
                            </Field>
                            <Field label="Excluded Outlets">
                                <Select isMulti components={animatedComponents} value={editedSelectedExOutlets} onChange={handleEditSelectChange} options={outletData} styles={selectStyles} placeholder="Select outlets to exclude…" closeMenuOnSelect={false} />
                            </Field>
                        </div>
                        <Field label="Description" required>
                            <textarea style={{ ...inputStyle, height: '72px', padding: '8px 10px', resize: 'vertical' }} name="description" value={editedData.description} placeholder="Brief description" onChange={handleEditChange} />
                        </Field>
                    </Modal.Body>
                    <Modal.Footer style={modalFooterStyle}>
                        <button style={ghostBtn} onClick={handleCloseEditModal}>Cancel</button>
                        <button style={primaryBtn} onClick={editCategory}>
                            {isEditLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Changes'}
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* ── DELETE MODAL ── */}
                <Modal show={openDeleteModal} onHide={handleCloseDeleteModal} centered>
                    <Modal.Header closeButton style={modalHeaderStyle}>
                        <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Delete Category</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '20px', fontSize: '14px', color: T.textMuted }}>
                        This will permanently delete the category. This action cannot be undone.
                    </Modal.Body>
                    <Modal.Footer style={modalFooterStyle}>
                        <button style={ghostBtn} onClick={handleCloseDeleteModal}>Cancel</button>
                        <button style={dangerBtn} onClick={deleteCategory}>
                            {isDeleteLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Delete'}
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* ── IMAGE PREVIEW MODAL ── */}
                <Modal show={showImageModal} onHide={handleCloseImageModal} centered>
                    <Modal.Header closeButton style={modalHeaderStyle}>
                        <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Category Image</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '20px', textAlign: 'center' }}>
                        {selectedImage ? (
                            <>
                                <img src={selectedImage?.displayImage} alt="preview" style={{ width: '50%', height: 'auto', borderRadius: '50%', border: `0.5px solid ${T.border}` }} />
                                <div style={{ marginTop: '16px' }}>
                                    <button style={ghostBtn} onClick={() => handleImageUploadModal({ displayImage: selectedImage?.displayImage, _id: selectedImage?._id })}>
                                        <i className="ri-image-edit-line" /> Change Image
                                    </button>
                                </div>
                            </>
                        ) : (
                            <p style={{ color: T.textFaint }}>No image uploaded.</p>
                        )}
                    </Modal.Body>
                </Modal>

                {/* ── BANNER PREVIEW MODAL ── */}
                <Modal show={showBannerImageModal} onHide={() => setShowBannerImageModal(false)} centered>
                    <Modal.Header closeButton style={modalHeaderStyle}>
                        <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Banner Image</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '20px' }}>
                        {selectedImage ? (
                            <>
                                <img src={selectedImage?.bannerImage} alt="banner preview" style={{ width: '100%', height: 'auto', borderRadius: '3px', border: `0.5px solid ${T.border}` }} />
                                <div style={{ marginTop: '14px' }}>
                                    <button style={ghostBtn} onClick={() => handleBannerImageUpload({ bannerImage: selectedImage?.bannerImage, _id: selectedImage?._id })}>
                                        <i className="ri-image-edit-line" /> Change Banner
                                    </button>
                                </div>
                            </>
                        ) : (
                            <p style={{ color: T.textFaint }}>No banner image uploaded.</p>
                        )}
                    </Modal.Body>
                </Modal>

                {/* ── UPDATE BANNER IMAGE MODAL ── */}
                {openBannerImageUploadModal && (
                    <Modal show={openBannerImageUploadModal} onHide={() => setOpenBannerImageUploadModal(false)} centered>
                        <Modal.Header closeButton style={modalHeaderStyle}>
                            <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Update Banner Image</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ padding: '20px' }}>
                            <Field label="Choose File">
                                <input style={{ ...inputStyle, height: 'auto', padding: '4px 8px' }} type="file" id="imageInput" accept="image/*" onChange={handleEditBannerImage} ref={imageInputRef} />
                                <span style={{ fontSize: '11px', color: T.red, marginTop: '4px' }}>Dimensions must be exactly 1000×200 px</span>
                            </Field>
                        </Modal.Body>
                        <Modal.Footer style={modalFooterStyle}>
                            <button style={primaryBtn} onClick={handleBannerEditImage}>
                                {isImageEditBtnLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Changes'}
                            </button>
                        </Modal.Footer>
                    </Modal>
                )}

                {/* ── UPDATE IMAGE MODAL ── */}
                {openImageUploadModal && (
                    <Modal show={openImageUploadModal} onHide={() => setOpenImageUploadModal(false)} centered>
                        <Modal.Header closeButton style={modalHeaderStyle}>
                            <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Update Category Image</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ padding: '20px' }}>
                            <Field label="Choose File">
                                <input style={{ ...inputStyle, height: 'auto', padding: '4px 8px' }} type="file" id="imageInput" accept="image/*" onChange={handleImageChange} />
                            </Field>
                        </Modal.Body>
                        <Modal.Footer style={modalFooterStyle}>
                            <button style={primaryBtn} onClick={handleEditImage}>
                                {isImageEditBtnLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Changes'}
                            </button>
                        </Modal.Footer>
                    </Modal>
                )}
                {/* ════════════════════════════════════════════════════════════
                    FEATURED — EDIT DETAILS MODAL
                ════════════════════════════════════════════════════════════ */}
                <Modal show={openFeaturedEditModal} onHide={() => setOpenFeaturedEditModal(false)} size="lg" centered>
                    <Modal.Header closeButton style={modalHeaderStyle}>
                        <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Update Featured Category</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' }}>
                            <Field label="Collection Name" required>
                                <input style={inputStyle} name="name" value={featuredEditData.name} type="text" placeholder="e.g. Summer Collection" onChange={handleFeaturedEditChange} />
                            </Field>
                            <Field label="Sequence No." required>
                                <input style={inputStyle} name="sequenceNumber" value={featuredEditData.sequenceNumber} type="number" onChange={handleFeaturedEditChange} />
                            </Field>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                            <Field label="Validity Start Date" required>
                                <DatePicker
                                    selected={featuredEditData.validityStartDate ? new Date(featuredEditData.validityStartDate) : null}
                                    onChange={(date) => setFeaturedEditData((p) => ({ ...p, validityStartDate: date ? date.toISOString().split('T')[0] : "" }))}
                                    dateFormat="yyyy-MM-dd" placeholderText="Select start date"
                                    isClearable customInput={<input style={inputStyle} readOnly />}
                                    wrapperStyle={{ width: '100%' }} popperPlacement="bottom-start"
                                />
                            </Field>
                            <Field label="Validity End Date" required>
                                <DatePicker
                                    selected={featuredEditData.validityEndDate ? new Date(featuredEditData.validityEndDate) : null}
                                    onChange={(date) => setFeaturedEditData((p) => ({ ...p, validityEndDate: date ? date.toISOString().split('T')[0] : "" }))}
                                    dateFormat="yyyy-MM-dd" placeholderText="Select end date"
                                    minDate={featuredEditData.validityStartDate ? new Date(featuredEditData.validityStartDate) : new Date()}
                                    isClearable customInput={<input style={inputStyle} readOnly />}
                                    wrapperStyle={{ width: '100%' }} popperPlacement="bottom-start"
                                />
                            </Field>
                        </div>
                        <Field label="Description" required>
                            <textarea style={{ ...inputStyle, height: '66px', padding: '8px 10px', resize: 'vertical' }} name="description" value={featuredEditData.description} placeholder="Brief description" onChange={handleFeaturedEditChange} />
                        </Field>

                        {/* Products */}
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                <label style={{ ...labelStyle, marginBottom: 0 }}>Products <span style={{ color: T.red }}>*</span></label>
                                <button style={{ ...ghostBtn, height: '26px', padding: '0 10px', fontSize: '12px' }} onClick={addEditProductRow}>
                                    <i className="ri-add-line" /> Add Product
                                </button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 1fr 1fr 36px', gap: '8px', marginBottom: '6px', padding: '0 2px' }}>
                                {['Product', 'Seq', 'Excluded Outlets', 'Included Outlets', ''].map((h) => (
                                    <span key={h} style={{ ...labelStyle, marginBottom: 0 }}>{h}</span>
                                ))}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                {featuredEditProducts.map((row, idx) => (
                                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 1fr 1fr 36px', gap: '8px', alignItems: 'center' }}>
                                        <Select
                                            options={filteredProductList?.map((rm) => ({ value: rm?._id, label: rm?.name }))}
                                            value={row.product ? { value: row.product, label: filteredProductList?.find((rm) => rm._id === row.product)?.name || row.product } : null}
                                            onChange={(sel) => handleEditProductRowChange(idx, 'product', sel ? sel.value : "")}
                                            components={animatedComponents}
                                            styles={{ ...selectStyles, control: (p, s) => ({ ...selectStyles.control(p, s), fontSize: '12px', minHeight: '34px' }) }}
                                            placeholder="Select product…" isClearable
                                        />
                                        <input style={{ ...inputStyle, fontSize: '12px' }} type="number" value={row.sequenceNumber} min={1} onChange={(e) => handleEditProductRowChange(idx, 'sequenceNumber', Number(e.target.value))} />
                                        <Select
                                            isMulti components={animatedComponents}
                                            value={outletData.filter(o => row.excludedOutlets.includes(o.value))}
                                            onChange={(sel) => handleEditProductOutletChange(idx, 'excludedOutlets', sel)}
                                            options={outletData}
                                            styles={{ ...selectStyles, control: (p, s) => ({ ...selectStyles.control(p, s), fontSize: '12px', minHeight: '34px' }) }}
                                            placeholder="Excluded…" closeMenuOnSelect={false}
                                        />
                                        <Select
                                            isMulti components={animatedComponents}
                                            value={outletData.filter(o => row.includedOutlets.includes(o.value))}
                                            onChange={(sel) => handleEditProductOutletChange(idx, 'includedOutlets', sel)}
                                            options={outletData}
                                            styles={{ ...selectStyles, control: (p, s) => ({ ...selectStyles.control(p, s), fontSize: '12px', minHeight: '34px' }) }}
                                            placeholder="Included…" closeMenuOnSelect={false}
                                        />
                                        <button onClick={() => removeEditProductRow(idx)} disabled={featuredEditProducts.length === 1}
                                            style={{ width: '36px', height: '34px', border: `0.5px solid ${T.borderMid}`, borderRadius: '4px', background: T.surface, cursor: featuredEditProducts.length === 1 ? 'not-allowed' : 'pointer', color: T.red, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: featuredEditProducts.length === 1 ? 0.4 : 1 }}>
                                            <i className="ri-delete-bin-line" style={{ fontSize: '14px' }} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer style={modalFooterStyle}>
                        <button style={ghostBtn} onClick={() => setOpenFeaturedEditModal(false)}>Cancel</button>
                        <button style={primaryBtn} onClick={updateFeaturedCategory}>
                            {isFeaturedEditLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Changes'}
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* ════════════════════════════════════════════════════════════
                    FEATURED — DELETE MODAL
                ════════════════════════════════════════════════════════════ */}
                <Modal show={openFeaturedDeleteModal} onHide={() => setOpenFeaturedDeleteModal(false)} centered>
                    <Modal.Header closeButton style={modalHeaderStyle}>
                        <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Delete Featured Category</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '20px', fontSize: '14px', color: T.textMuted }}>
                        Are you sure you want to delete <strong style={{ color: T.textPrimary }}>{selectedFeatured?.name}</strong>? This action cannot be undone.
                    </Modal.Body>
                    <Modal.Footer style={modalFooterStyle}>
                        <button style={ghostBtn} onClick={() => setOpenFeaturedDeleteModal(false)}>Cancel</button>
                        <button style={dangerBtn} onClick={deleteFeaturedCategory}>
                            {isFeaturedDeleteLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Delete'}
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* ════════════════════════════════════════════════════════════
                    FEATURED — UPDATE IMAGE MODAL
                ════════════════════════════════════════════════════════════ */}
                <Modal show={openFeaturedImgModal} onHide={() => { setOpenFeaturedImgModal(false); setFeaturedImage(null); }} centered>
                    <Modal.Header closeButton style={modalHeaderStyle}>
                        <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Update Category Image</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {selectedFeatured?.displayImage && (
                            <img src={selectedFeatured.displayImage} alt="current" style={{ width: '80px', height: '80px', borderRadius: '50%', objectFit: 'cover', border: `0.5px solid ${T.border}`, alignSelf: 'center' }} />
                        )}
                        <Field label="Choose New Image">
                            <input style={{ ...inputStyle, height: 'auto', padding: '4px 8px' }} type="file" accept="image/*" onChange={(e) => setFeaturedImage(e.target.files[0])} />
                        </Field>
                    </Modal.Body>
                    <Modal.Footer style={modalFooterStyle}>
                        <button style={ghostBtn} onClick={() => { setOpenFeaturedImgModal(false); setFeaturedImage(null); }}>Cancel</button>
                        <button style={primaryBtn} onClick={updateFeaturedImage} disabled={!featuredImage}>
                            {isFeaturedImgLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Changes'}
                        </button>
                    </Modal.Footer>
                </Modal>

                {/* ════════════════════════════════════════════════════════════
                    FEATURED — UPDATE BANNER IMAGE MODAL
                ════════════════════════════════════════════════════════════ */}
                <Modal show={openFeaturedBannerModal} onHide={() => { setOpenFeaturedBannerModal(false); setFeaturedBannerImage(null); }} centered>
                    <Modal.Header closeButton style={modalHeaderStyle}>
                        <Modal.Title style={{ fontSize: '15px', fontWeight: '500' }}>Update Banner Image</Modal.Title>
                    </Modal.Header>
                    <Modal.Body style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        {selectedFeatured?.bannerImage && (
                            <img src={selectedFeatured.bannerImage} alt="current banner" style={{ width: '100%', height: '60px', objectFit: 'cover', borderRadius: '3px', border: `0.5px solid ${T.border}` }} />
                        )}
                        <Field label="Choose New Banner (1000×200 px)">
                            <input style={{ ...inputStyle, height: 'auto', padding: '4px 8px' }} type="file" accept="image/*" onChange={(e) => setFeaturedBannerImage(e.target.files[0])} />
                            <span style={{ fontSize: '11px', color: T.red, marginTop: '2px' }}>Dimensions must be exactly 1000×200 px</span>
                        </Field>
                    </Modal.Body>
                    <Modal.Footer style={modalFooterStyle}>
                        <button style={ghostBtn} onClick={() => { setOpenFeaturedBannerModal(false); setFeaturedBannerImage(null); }}>Cancel</button>
                        <button style={primaryBtn} onClick={updateFeaturedBannerImage} disabled={!featuredBannerImage}>
                            {isFeaturedImgLoading ? <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> : 'Save Changes'}
                        </button>
                    </Modal.Footer>
                </Modal>

            </div>
        </React.Fragment>
    );
};

export default Category;